import { useCallback, useState } from 'react';

import { DEFAULT_ERROR_MESSAGE } from '@/config/text.const';
import { useSaveFileLocally } from '@/hooks/useSaveFileLocally.hook';
import { contractsHttpClient } from '@/services/http/clients/contractsHttpClient';
import { notificationService } from '@/services/notification/notification.client';

const MAX_RETRY_COUNT = 15; //this will give 10 minutes retry
const BASE_RETRY_DELAY = 5000; //ms
const FIRST_RETRY_INTERVAL = 3000; //ms

export default function useExportUsageDetailsFile() {
  const [fileIsLoading, setFileIsLoading] = useState(true);
  const { saveFileLocally } = useSaveFileLocally();

  const downloadFile = async (id: UUID) => {
    try {
      if (!id) {
        throw new Error('Invalid file id');
      }
      const requestUrl = `/exports/${id}`;
      const response = await contractsHttpClient.get(requestUrl, {
        responseType: 'arraybuffer',
      });
      return response;
    }
    catch (err: any) {
      setFileIsLoading(false);
      return notificationService.open({
        title: 'Error',
        text: err?.message ?? DEFAULT_ERROR_MESSAGE,
        type: 'danger'
      });
    }
  };

  const downloadAndRetry = useCallback((id: string, fileName: string) => {
    let retryCount = 0;
    const downloadExportFileIfReady = async () => {
      try {
        setFileIsLoading(true);
        const response = await downloadFile(id);

        if (response && response.status === 200) {
          //download ready
          const blob = new Blob([response.data]);
          const fileSaved = saveFileLocally(blob, fileName);
          setFileIsLoading(false);
          if (fileSaved) {
            notificationService.open({
              title: 'Export to Excel',
              text: 'Usage details successfully exported',
              type: 'success'
            });
          }
        } else if (response && response.status === 204 && retryCount < MAX_RETRY_COUNT) {
          //download not ready, should retry
          retryCount++;
          const nextRetryDelay = BASE_RETRY_DELAY * retryCount;
          setTimeout(downloadExportFileIfReady, nextRetryDelay);
        }
      } catch (error: any) {
        setFileIsLoading(false);
        return notificationService.open({
          title: 'Export to Excel',
          text: error?.message ?? 'Export to Excel failed',
          type: 'danger'
        });
      }
    };
    setTimeout(downloadExportFileIfReady, FIRST_RETRY_INTERVAL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return {
    downloadAndRetry,
    fileIsLoading,
    setFileIsLoading
  } as const;
}

